/**
 * AttendanceReports — curated report library + live preview.
 *
 * Migrated to live API in Wave 3.2. Reads:
 *   GET  /v1/attendance/reports                   → backend-known ids
 *   POST /v1/attendance/reports/{id}/generate     → queues a job
 *   GET  /v1/attendance/reports/{id}/preview      → { columns, rows, total }
 *
 * The curated 18-report catalog and the backend definitions are currently in
 * parity. Availability is intentionally tri-state: unresolved metadata is not
 * treated as unavailable, so the first render never flashes a misleading
 * Preview chip while the definitions request is still in flight.
 *
 * Column / row rendering is data-driven from the ReportPreview
 * payload (columns: string[], rows: list[dict]), so the same page
 * covers any shape the backend settles on.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Avatar, Box, Button, Chip, FormControlLabel, Switch, TableFooter,
  FormControl, IconButton, InputLabel, LinearProgress, Menu,
  MenuItem, Paper, Select, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  ApartmentOutlined, ArrowBack, ArrowDropDownOutlined,
  AssignmentOutlined, BeachAccessOutlined, CalendarMonthOutlined,
  CallMissedOutgoingOutlined, EventBusyOutlined, ExitToAppOutlined,
  FileDownloadOutlined, FreeBreakfastOutlined, HistoryOutlined,
  MoreTimeOutlined, PeopleOutlined, PhoneIphoneOutlined,
  RefreshOutlined, RunningWithErrorsOutlined, ScheduleOutlined,
  SwapHorizOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import { useTranslation } from 'react-i18next';
import { formatReportCell, isNumericColumn } from './reportCell';
import DateField from '../../../components/inputs/DateField';
import { translateApiError } from '../../../i18n/translateApiError';
import {
  buildExportFilename,
  exportTable,
  type ExportFormat,
} from '../../../utils/exportTable';
import type {
  ReportDefinition, ReportPreview,
} from '../../../api/attendance';
import type {
  DepartmentNode, WorkerResponse,
} from '../../../api/organization';

// ---------------------------------------------------------------------------
// Curated catalog — preserved verbatim from the Wave 3.1 mock so users
// see the same library. Backend-known ids short-circuit to real preview;
// unknown ids still call through (backend returns empty) with a helpful
// hint in the empty-state card.
// ---------------------------------------------------------------------------

/**
 * Which DAYS a report contains, stated per report because the four answers are genuinely
 * different and nothing on screen said which one applied. The 3-rows-for-a-month result an owner
 * reported on 2026-08-12 was correct behaviour for a punch log and read as data loss.
 *
 *   punchLog  — only days with a punch. An audit trail of events that happened.
 *   perDay    — every calendar day per worker, rest and holiday included. Payroll reconciliation:
 *               a gap has to be visible AS a gap.
 *   exception — only the offending days. That is the definition of the report.
 *   period    — one row per worker (or per day and department). The answer, not the evidence.
 *   auditLog  — change records, not attendance days.
 */
export type ReportFamily = 'punchLog' | 'perDay' | 'exception' | 'period' | 'auditLog';

interface CatalogReport {
  id: string;
  icon: React.ReactNode;
  formats: string[];
  family: ReportFamily;
}

interface CatalogCategory { id: string; reports: CatalogReport[] }

const CATALOG_STATIC: CatalogCategory[] = [
  {
    id: 'clockInRecords',
    reports: [
      { id: 'all_records', icon: <SwapHorizOutlined />, formats: ['Excel', 'CSV'], family: 'punchLog' },
      { id: 'daily_timesheet', icon: <ScheduleOutlined />, formats: ['Excel', 'CSV'], family: 'perDay' },
      { id: 'first_last_punch', icon: <SwapHorizOutlined />, formats: ['Excel', 'CSV'], family: 'punchLog' },
      { id: 'clock_in_locations', icon: <PhoneIphoneOutlined />, formats: ['Excel', 'PDF'], family: 'punchLog' },
      { id: 'missed_clock_ins', icon: <CallMissedOutgoingOutlined />, formats: ['Excel', 'CSV'], family: 'exception' },
    ],
  },
  {
    id: 'attendanceIssues',
    reports: [
      { id: 'late_arrivals', icon: <RunningWithErrorsOutlined />, formats: ['Excel', 'PDF'], family: 'exception' },
      { id: 'early_departures', icon: <ExitToAppOutlined />, formats: ['Excel', 'PDF'], family: 'exception' },
      { id: 'absences', icon: <EventBusyOutlined />, formats: ['Excel', 'PDF'], family: 'exception' },
      { id: 'overtime_hours', icon: <MoreTimeOutlined />, formats: ['Excel', 'CSV'], family: 'exception' },
      { id: 'break_time', icon: <FreeBreakfastOutlined />, formats: ['Excel', 'CSV'], family: 'exception' },
    ],
  },
  {
    id: 'summariesPayroll',
    reports: [
      { id: 'monthly_summary', icon: <PeopleOutlined />, formats: ['Excel', 'PDF'], family: 'period' },
      // The per-day drill-down sits beside the summary it explains. It used to live under Audit &
      // Compliance with `edit_history`, two groups away, so the natural motion — a person's number
      // looks wrong, open their days — crossed unrelated groups.
      { id: 'daily_detail', icon: <ScheduleOutlined />, formats: ['Excel', 'PDF'], family: 'perDay' },
      { id: 'department_attendance', icon: <ApartmentOutlined />, formats: ['Excel', 'PDF'], family: 'period' },
      { id: 'monthly_trends', icon: <CalendarMonthOutlined />, formats: ['Excel', 'PDF'], family: 'period' },
      { id: 'leave_report', icon: <BeachAccessOutlined />, formats: ['Excel', 'CSV'], family: 'exception' },
      { id: 'overtime_summary', icon: <MoreTimeOutlined />, formats: ['Excel', 'CSV'], family: 'period' },
      { id: 'payroll_export', icon: <AssignmentOutlined />, formats: ['CSV'], family: 'period' },
    ],
  },
  {
    id: 'auditCompliance',
    reports: [
      { id: 'edit_history', icon: <HistoryOutlined />, formats: ['Excel', 'CSV'], family: 'auditLog' },
    ],
  },
];

const ALL_REPORT_IDS = CATALOG_STATIC.flatMap((c) => c.reports.map((r) => r.id));

// ---------------------------------------------------------------------------
// Date helpers (mirrors AttendanceCalculation so the two pages share the
// same 6-preset quick-period vocabulary; FILTER_DESIGN_CONVENTION §9
// requires presets to live in the Header row and editing either input
// flips the select to "Custom").
// ---------------------------------------------------------------------------

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function monthStart() { const d = new Date(); d.setDate(1); return isoDate(d); }
function monthEnd() { const d = new Date(); d.setMonth(d.getMonth() + 1); d.setDate(0); return isoDate(d); }
function lastMonth(): [string, string] {
  const d = new Date();
  d.setDate(1);
  const end = new Date(d);
  end.setDate(0);
  d.setMonth(d.getMonth() - 1);
  return [isoDate(d), isoDate(end)];
}

const periodOptions = [
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_30_days', label: 'Last 30 Days' },
];

function periodRange(v: string): [string, string] | null {
  const today = new Date();
  switch (v) {
    case 'this_week': {
      const d = new Date(today);
      const day = (d.getDay() + 6) % 7; // Mon=0
      d.setDate(d.getDate() - day);
      const e = new Date(d); e.setDate(d.getDate() + 6);
      return [isoDate(d), isoDate(e)];
    }
    case 'last_week': {
      const d = new Date(today);
      const day = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - day - 7);
      const e = new Date(d); e.setDate(d.getDate() + 6);
      return [isoDate(d), isoDate(e)];
    }
    case 'last_7_days': {
      const e = new Date(today);
      const s = new Date(today); s.setDate(s.getDate() - 6);
      return [isoDate(s), isoDate(e)];
    }
    case 'this_month': return [monthStart(), monthEnd()];
    case 'last_month': return lastMonth();
    case 'last_30_days': {
      const e = new Date(today);
      const s = new Date(today); s.setDate(s.getDate() - 29);
      return [isoDate(s), isoDate(e)];
    }
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initials(name: string | null | undefined): string {
  if (!name) return '•';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase() || '•';
}

function fullName(e: WorkerResponse): string {
  return `${e.first_name} ${e.last_name}`.trim();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AttendanceReports: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const reportName = (id: string) => t(`attendance:attendanceReports.catalog.${id}.name`);
  const reportDesc = (id: string) => t(`attendance:attendanceReports.catalog.${id}.description`);
  const catName = (id: string) => t(`attendance:attendanceReports.categories.${id}`);

  // Localize a backend-supplied column key (snake_case) into a human header.
  // Known keys come from attendance:attendanceReports.columns.*; anything the
  // backend adds later degrades to a prettified Title Case so a raw
  // snake_case key never leaks onto the screen (owner report 2026-06-17:
  // all report table headers were un-translated raw keys).
  // Passed to every cell. `i18n.language` rather than a hardcoded locale: the weekday and the
  // month name in a report date have to be the reader's. Read defensively — a component must not
  // crash because a locale is unavailable, and the date formatter already falls back to the ISO
  // string on a bad tag.
  // Rest days stay visible by DEFAULT. Hiding them by default is how a reconciliation report
  // starts lying by omission — a gap has to be visible as a gap. But 86 of one tenant's 146 rows
  // were `rest`, so the option to collapse them is what makes the report readable.
  const [showRestDays, setShowRestDays] = useState(true);

  /**
   * Open Daily Detail for one worker, keeping the selected range.
   *
   * Switches the active report rather than navigating: the range, the location and the department
   * filters are already on this page, and a route would have to re-encode all of them. Selecting
   * the worker is what makes the drill-down about one person.
   */
  const drillToDailyDetail = useCallback((workerName: string) => {
    const target = CATALOG_STATIC.flatMap((c) => c.reports).find((r) => r.id === 'daily_detail');
    if (!target) return;
    setSelectedWorkers([workerName]);
    setPreview(null);
    setPage(0);
    setActive(target);
  }, []);

  const cellCtx = useMemo(() => ({ t: t as unknown as (k: string, o?: Record<string, unknown>) => string, locale: i18n?.language || 'en' }), [t, i18n?.language]);

  /** Chip colour per status / deduction reason — palette tokens only (svdy-ui §6). */
  const statusTone = (v: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    if (['normal', 'present'].includes(v)) return 'success';
    if (['late', 'early_leave', 'half_day', 'short', 'late_half_day', 'early_half_day',
         'half_day_leave'].includes(v)) return 'warning';
    if (['absent', 'absent_no_show', 'late_full_day', 'early_full_day'].includes(v)) return 'error';
    if (['leave', 'full_day_leave', 'pending_review', 'business_trip'].includes(v)) return 'info';
    return 'default';   // rest / holiday / unscheduled — not an exception, so not coloured
  };

  const colLabel = useCallback(
    (key: string): string => {
      const label = t(`attendance:attendanceReports.columns.${key}`, { defaultValue: '' });
      if (label) return label;
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase());
    },
    [t],
  );

  const [backendIds, setBackendIds] = useState<Set<string> | null>(null);
  const [active, setActive] = useState<CatalogReport | null>(null);
  const [depts, setDepts] = useState<DepartmentNode[]>([]);
  const [workers, setWorkers] = useState<WorkerResponse[]>([]);

  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(monthEnd());
  const [quickPeriod, setQuickPeriod] = useState('this_month');
  const [selectedDepts, setSelectedDepts] = useState<string[]>([]);
  const [selectedWorkers, setSelectedWorkers] = useState<string[]>([]);
  const [loc, setLoc] = useState('All');

  const [generating, setGenerating] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [preview, setPreview] = useState<ReportPreview | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  // ---------- period ----------
  const handleQuickPeriod = (v: string) => {
    setQuickPeriod(v);
    const r = periodRange(v);
    if (r) {
      setStartDate(r[0]);
      setEndDate(r[1]);
      setPreview(null);
    }
  };

  const handleDateChange = (field: 'start' | 'end', value: string) => {
    if (field === 'start') setStartDate(value);
    else setEndDate(value);
    setQuickPeriod('custom');
    setPreview(null);
  };

  // Resolve availability independently from filter reference data. A failed
  // departments/workers request must not turn every live report into Preview.
  const fetchBootstrap = useCallback(async () => {
    setError(null);
    setBackendIds(null);
    const [reportsResult, deptsResult, workersResult] = await Promise.allSettled([
      attendanceApi.reportsApi.list() as Promise<ReportDefinition[]>,
      organizationApi.departmentsApi.list(),
      organizationApi.workersApi.list('active'),
    ]);

    if (reportsResult.status === 'fulfilled') {
      setBackendIds(new Set(reportsResult.value.map((report) => report.id)));
    }
    if (deptsResult.status === 'fulfilled') setDepts(deptsResult.value);
    if (workersResult.status === 'fulfilled') setWorkers(workersResult.value);

    const failed = [reportsResult, deptsResult, workersResult]
      .find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failed) {
      setError(failed.reason instanceof ApiError
        ? failed.reason.message
        : t('common:feedback.loadFailed'));
    }
  }, [t]);

  useEffect(() => { fetchBootstrap(); }, [fetchBootstrap]);

  const handleSelect = (r: CatalogReport) => {
    setActive(r);
    setPreview(null);
    setPage(0);
  };

  const handleBack = () => {
    setActive(null);
    setPreview(null);
  };

  // Map the multi-select Department picker (catalog stores names) to an id
  // when there's exactly one selection. The preview/export endpoints accept
  // a single department_id; multi-select narrows is post-fetch (client-side
  // doesn't apply here yet — kept parity with the existing UI).
  const departmentIdParam = useMemo(() => {
    if (selectedDepts.length !== 1) return undefined;
    const match = depts.find((d) => d.name === selectedDepts[0]);
    return match?.id;
  }, [selectedDepts, depts]);

  const fetchPreview = useCallback(async () => {
    if (!active) return null;
    return attendanceApi.reportsApi.preview(active.id, {
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      department_id: departmentIdParam,
    });
  }, [active, startDate, endDate, departmentIdParam]);

  const handleGenerate = async () => {
    if (!active || generating) return;
    setGenerating(true);
    setError(null);
    try {
      // Kick off the job (fire-and-forget; backend is a stub that
      // returns 'queued → completed' immediately).
      await attendanceApi.reportsApi.generate(active.id);
      const p = await fetchPreview();
      if (p) setPreview(p);
      setPage(0);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:reports.generateFailed'));
    } finally {
      setGenerating(false);
    }
  };

  // Inline calculation for the "needs_calculation" banner. When the selected
  // range has no daily summaries, kicking off /calculation/jobs + polling
  // /calculation/status until completed/failed before re-fetching preview.
  // The poll loop matters in production where ARQ_ENABLED=True returns
  // 'queued' immediately; in local dev (ARQ_ENABLED=false) the inline
  // fallback returns 'completed' in one trip and we exit the loop on the
  // first iteration.
  const handleCalculateNow = async () => {
    if (!active || calculating) return;
    setCalculating(true);
    setError(null);
    try {
      const queued = await attendanceApi.calculationApi.run({
        start_date: startDate,
        end_date: endDate,
        department_id: departmentIdParam ?? null,
      });
      // Poll up to ~16s (20 * 800ms). Backend stub flips to completed on
      // the first poll; real arq runs settle within a few iterations for
      // typical org sizes.
      let status = queued.status;
      const jobId = queued.job_id;
      for (let i = 0; i < 20 && status !== 'completed' && status !== 'failed'; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 800));
        // eslint-disable-next-line no-await-in-loop
        const s = await attendanceApi.calculationApi.status(jobId);
        status = s.status;
      }
      if (status === 'failed') {
        setError(t('attendance:attendanceReports.calcJobFailed'));
        return;
      }
      const p = await fetchPreview();
      if (p) {
        setPreview(p);
        setPage(0);
      }
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:reports.calculationFailed'));
    } finally {
      setCalculating(false);
    }
  };

  const handleExport = async (format: string) => {
    setExportAnchor(null);
    if (!active || !preview) return;
    if (preview.needs_calculation) {
      setToast(t('attendance:attendanceReports.exportFirst'));
      return;
    }
    if (!preview.rows?.length) {
      setToast(t('attendance:attendanceReports.exportEmpty'));
      return;
    }
    try {
      const rawCols = preview.columns ?? [];
      const localizedCols = rawCols.map(colLabel);
      // The SAME formatter the table uses. This used to pass `r[c]` straight through, so the
      // headers were localized and the values were not — the CSV a customer forwards to their
      // accountant carried `2026-08-07T00:56:56.844972+00:00` and `early_leave`.
      const localizedRows = ((preview.rows ?? []) as Record<string, unknown>[]).map((r) => {
        const o: Record<string, unknown> = {};
        rawCols.forEach((c) => { o[colLabel(c)] = formatReportCell(c, r[c], cellCtx); });
        return o;
      });
      await exportTable(format as ExportFormat, {
        filename: buildExportFilename(reportName(active.id), startDate, endDate),
        title: reportName(active.id),
        columns: localizedCols,
        rows: localizedRows,
      });
      setToast(t('attendance:attendanceReports.exportDone', { name: reportName(active.id), format }));
    } catch (e) {
      setToast(e instanceof Error ? e.message : t('attendance:reports.exportFailed'));
    }
  };

  const columns = preview?.columns ?? [];
  const activeFamily: ReportFamily = useMemo(
    () => CATALOG_STATIC.flatMap((c) => c.reports).find((r) => r.id === active?.id)?.family
      ?? 'perDay',
    [active?.id],
  );
  const allRows = useMemo(
    () => (preview?.rows ?? []) as Record<string, unknown>[],
    [preview],
  );
  const rows = useMemo(() => {
    // Rest and holiday rows are hidden only when the admin asks, and only on the per-day family
    // — the family whose whole point is that every calendar day is present.
    if (showRestDays || activeFamily !== 'perDay') return allRows;
    return allRows.filter((r) => r.status !== 'rest' && r.status !== 'holiday');
  }, [allRows, showRestDays, activeFamily]);
  // Read straight off the typed response. This used to be
  //   (preview as { totals?: Record<string, unknown> } | null)?.totals ?? null
  // and the cast was the bug's hiding place: `ReportPreviewResponse` did not declare
  // `totals`, so Pydantic v2 stripped it from every response body (svdy-rules §6) and
  // this line resolved to null forever — the totals block built on 2026-08-12 never
  // rendered once. A cast that silences a missing field also silences its absence.
  const totals = preview?.totals ?? null;
  const paginated = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage],
  );

  // ---------- DETAIL VIEW ----------
  if (active) {
    const backendKnown = backendIds?.has(active.id);
    const isUnavailable = backendKnown === false;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
        {/* Header */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={handleBack} sx={{ mr: 0.5 }}><ArrowBack fontSize="small" /></IconButton>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>
                  {reportName(active.id)}
                </Typography>
                {isUnavailable && (
                  <Chip size="small" label={t('attendance:reports.preview')} color="warning" variant="outlined"
                    sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">{reportDesc(active.id)}</Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Date range + quick-period live in Header per FILTER_DESIGN_CONVENTION §9 */}
            <Stack direction="row" alignItems="center" sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, overflow: 'hidden', height: 32 }}>
              <DateField value={startDate} onChange={(e) => handleDateChange('start', e.target.value)} size="small"
                sx={{ width: 135, '& fieldset': { border: 'none' }, '& .MuiInputBase-root': { height: 30, fontSize: '0.8rem' } }} />
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.25 }}>–</Typography>
              <DateField value={endDate} onChange={(e) => handleDateChange('end', e.target.value)} size="small"
                sx={{ width: 135, '& fieldset': { border: 'none' }, '& .MuiInputBase-root': { height: 30, fontSize: '0.8rem' } }} />
            </Stack>
            <Select value={quickPeriod} onChange={(e) => handleQuickPeriod(e.target.value)} size="small"
              sx={{ minWidth: 130, height: 32, fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.5, fontWeight: 600 } }}>
              {periodOptions.map((o) => <MenuItem key={o.value} value={o.value}>{t('common:periodOptions.' + o.value)}</MenuItem>)}
              <MenuItem value="custom">{t('attendance:attendanceReports.custom')}</MenuItem>
            </Select>
            {preview && !preview.needs_calculation && (
              <>
                <Button variant="outlined" size="small" startIcon={<FileDownloadOutlined />} endIcon={<ArrowDropDownOutlined />}
                  onClick={(e) => setExportAnchor(e.currentTarget)}
                  sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', color: 'text.primary', borderColor: 'rgba(0,0,0,0.2)', bgcolor: '#ffffff', '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: '#ffffff' } }}>
                  {t('common:actions.export')}
                </Button>
                <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
                  {(active.formats.length ? active.formats : ['CSV']).map((f) => (
                    <MenuItem key={f} onClick={() => handleExport(f)} sx={{ fontSize: '0.85rem' }}>{f}</MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Stack>
        </Box>

        {/* Filter row — Where → Who → What per FILTER_DESIGN_CONVENTION */}
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('attendance:attendanceReports.locationLabel')}</InputLabel>
            <Select value={loc} label={t('attendance:attendanceReports.locationLabel')} onChange={(e) => { setLoc(e.target.value); setPreview(null); }}>
              <MenuItem value="All">{t('attendance:attendanceReports.all', { defaultValue: 'All' })}</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete multiple size="small"
            options={depts.map((d) => d.name)}
            value={selectedDepts}
            onChange={(_, v) => { setSelectedDepts(v); setPreview(null); }}
            renderTags={(value, getTagProps) => value.map((opt, idx) => (
              <Chip {...getTagProps({ index: idx })} key={opt} label={opt} size="small"
                sx={{ borderRadius: 1, height: 22, fontSize: '0.7rem' }} />
            ))}
            renderInput={(params) => <TextField {...params} label={t('attendance:reports.departmentLabel')} placeholder={selectedDepts.length ? '' : t('attendance:reports.all')} />}
            sx={{ minWidth: 200 }} />
          <Autocomplete multiple size="small"
            options={workers.map((e) => fullName(e))}
            value={selectedWorkers}
            onChange={(_, v) => { setSelectedWorkers(v); setPreview(null); }}
            renderTags={(value, getTagProps) => value.map((opt, idx) => (
              <Chip {...getTagProps({ index: idx })} key={opt} label={opt} size="small"
                sx={{ borderRadius: 1, height: 22, fontSize: '0.7rem' }} />
            ))}
            renderInput={(params) => <TextField {...params} label={t('attendance:reports.workerLabel')} placeholder={selectedWorkers.length ? '' : t('attendance:reports.all')} />}
            sx={{ minWidth: 220 }} />
          <Button variant="contained" size="small" onClick={handleGenerate} disabled={generating}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', height: 40, whiteSpace: 'nowrap' }}>
            {generating ? t('attendance:attendanceReports.generating') : t('attendance:attendanceReports.generate')}
          </Button>
        </Box>

        {(generating || calculating) && <LinearProgress sx={{ width: '100%', mb: 1.5, borderRadius: 2 }} />}
        {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

        {preview ? (
          preview.needs_calculation ? (
            <Alert
              severity="warning"
              sx={{ width: '100%', mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleCalculateNow}
                  disabled={calculating || generating}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {calculating ? t('attendance:attendanceReports.calculatingNow') : t('attendance:attendanceReports.calculateNow')}
                </Button>
              }
            >
              <span dangerouslySetInnerHTML={{
                __html: t('attendance:attendanceReports.needsCalculation', {
                  start: preview.range?.start_date ?? startDate,
                  end: preview.range?.end_date ?? endDate,
                }),
              }} />
            </Alert>
          ) : (
          <>
            {/* Which days this report contains. The 3-rows-for-a-month result an owner reported
                was correct for a punch log and read as data loss, because nothing said so. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t(`attendance:attendanceReports.coverage.${activeFamily}`)}
            </Typography>
            {activeFamily === 'perDay' && (
              <FormControlLabel
                sx={{ mb: 0.5 }}
                control={(
                  <Switch
                    size="small"
                    checked={showRestDays}
                    onChange={(e) => { setShowRestDays(e.target.checked); setPage(0); }}
                  />
                )}
                label={(
                  <Typography variant="caption" color="text.secondary">
                    {t('attendance:attendanceReports.showRestDays')}
                  </Typography>
                )}
              />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t('attendance:attendanceReports.showingResults', { count: rows.length, start: startDate, end: endDate })}
                {selectedDepts.length ? ` · ${t('attendance:attendanceReports.deptCountSuffix', { count: selectedDepts.length })}` : ''}
                {selectedWorkers.length ? ` · ${t('attendance:attendanceReports.workerCountSuffix', { count: selectedWorkers.length })}` : ''}
              </Typography>
              <TablePagination component="div" count={rows.length} page={page}
                onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }} />
            </Box>
            <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
              <Table>
                <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
                  <TableRow>
                    {columns.length ? columns.map((c) => <TableCell key={c}>{colLabel(c)}</TableCell>) : <TableCell>—</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length || 1} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          {isUnavailable
                            ? t('attendance:attendanceReports.previewHint')
                            : t('attendance:attendanceReports.noRowsReturned')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((row, i) => (
                      <TableRow key={`${i}`} hover>
                        {columns.map((c) => {
                          if (c === 'worker' || c === 'worker_name') {
                            const name = String(row[c] ?? '—');
                            // From a period summary, a worker's name opens THEIR days over the same
                            // range. The motion an admin actually makes — "this person's number
                            // looks wrong, show me the days" — had no path at all.
                            const canDrill = activeFamily === 'period' && name !== '—';
                            const cell = (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                                  {initials(name)}
                                </Avatar>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, ...(canDrill ? { color: 'primary.main' } : null) }}
                                >
                                  {name}
                                </Typography>
                              </Box>
                            );
                            return (
                              <TableCell key={c}>
                                {canDrill ? (
                                  <Tooltip title={t('attendance:attendanceReports.drillToDaily', { name })}>
                                    <Box
                                      component="button"
                                      type="button"
                                      onClick={() => drillToDailyDetail(name)}
                                      sx={{
                                        p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer',
                                        font: 'inherit', textAlign: 'left',
                                      }}
                                    >
                                      {cell}
                                    </Box>
                                  </Tooltip>
                                ) : cell}
                              </TableCell>
                            );
                          }
                          const v = row[c];
                          // ONE formatter, shared with the export. This was
                          // `String(v)`, which is why a punch time rendered as
                          // `2026-08-07T00:56:56.844972+00:00` and a status as `early_leave`.
                          const display = formatReportCell(c, v, cellCtx);
                          if (c === 'status' || c === 'deduction_reason') {
                            return (
                              <TableCell key={c}>
                                {v == null || v === '' ? (
                                  <Typography variant="body2" color="text.disabled">—</Typography>
                                ) : (
                                  <Chip
                                    label={display}
                                    size="small"
                                    variant="outlined"
                                    color={statusTone(String(v))}
                                    sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
                                  />
                                )}
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={c} align={isNumericColumn(c) ? 'right' : 'left'}>
                              {display}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
                {totals && (
                  <TableFooter
                    sx={{
                      position: 'sticky', bottom: 0, bgcolor: 'grey.50',
                      '& .MuiTableCell-root': {
                        fontWeight: 700, color: 'text.primary', borderTop: '2px solid',
                        borderTopColor: 'divider', fontSize: '0.8125rem',
                      },
                    }}
                  >
                    {/* A totals BLOCK from the response, not a magic last row: a totals row inside
                        the data sorts with it, paginates into the middle of page 3, and lands
                        somewhere arbitrary in a CSV. */}
                    <TableRow>
                      {columns.map((c, idx) => {
                        if (idx === 0) {
                          return (
                            <TableCell key={c}>
                              {t('attendance:attendanceReports.totalsLabel', {
                                count: (totals._rows as number) ?? rows.length,
                              })}
                            </TableCell>
                          );
                        }
                        const v = totals[c];
                        // `authorized` is a boolean column, so its SUM is meaningless and
                        // the shared formatter would render the count `3` as "Yes". What
                        // belongs here is the exception count, and it needs its own
                        // sentence: a bare `3` beneath a header reading "Approved" says
                        // the opposite of what it means.
                        //
                        // It lives in this cell rather than as its own totals key because
                        // the footer iterates COLUMNS — a totals key that is not also a
                        // column is computed, serialised and never drawn.
                        if (c === 'authorized') {
                          const unapproved = totals.days_unauthorized_ot;
                          return (
                            <TableCell key={c} align="left">
                              {typeof unapproved === 'number'
                                ? t('attendance:attendanceReports.totalsUnapprovedOt',
                                    { count: unapproved })
                                : ''}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={c} align={isNumericColumn(c) ? 'right' : 'left'}>
                            {v === undefined || v === null ? '' : formatReportCell(c, v, cellCtx)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
          </>
          )
        ) : (
          <Paper elevation={0} sx={{ width: '100%', p: 6, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ color: 'text.disabled', mb: 1 }}>{active.icon}</Box>
            <Typography variant="body2" color="text.secondary">
              {isUnavailable
                ? t('attendance:reports.previewHint')
                : t('attendance:reports.filterHint')}
            </Typography>
          </Paper>
        )}

        <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ---------- LIBRARY VIEW ----------
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:attendanceReports.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {backendIds
              ? t('attendance:attendanceReports.librarySubtitle', { total: ALL_REPORT_IDS.length, live: backendIds.size })
              : t('attendance:attendanceReports.librarySubtitlePending', { total: ALL_REPORT_IDS.length })}
          </Typography>
        </Box>
        <Tooltip title={t('common:actions.refresh')}>
          <IconButton size="small" onClick={fetchBootstrap}
            sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
            <RefreshOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {CATALOG_STATIC.map((cat) => (
        <Box key={cat.id} sx={{ width: '100%', mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', mb: 1.5 }}>
            {catName(cat.id)}
          </Typography>
          {/* CSS Grid (svdy-ui §11.1) — auto-fill keeps the report tiles
              evenly distributed without MUI Grid container negative-margin
              left-shift. */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
              lg: 'repeat(6, 1fr)',
            },
            gap: 1.5,
          }}>
            {cat.reports.map((r) => {
              const known = backendIds?.has(r.id);
              return (
                <Paper key={r.id} sx={{
                  p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', cursor: 'pointer',
                  textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 0.75,
                  transition: 'box-shadow 80ms ease, border-color 80ms ease',
                  '&:hover': { borderColor: 'primary.main' },
                }} onClick={() => handleSelect(r)}>
                  <Box sx={{ color: 'primary.main' }}>{r.icon}</Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.3 }}>
                    {reportName(r.id)}
                  </Typography>
                  {known === false && (
                    <Chip label={t('attendance:reports.preview')} size="small" color="warning" variant="outlined"
                      sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />
                  )}
                </Paper>
              );
            })}
          </Box>
        </Box>
      ))}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        {t('attendance:attendanceReports.payrollSyncHint')}{' '}
        <Typography component="span" variant="caption"
          sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate('/integrations')}>
          {t('attendance:attendanceReports.integrationsLink')}
        </Typography>
      </Typography>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
        {t('attendance:attendanceReports.needReport')}{' '}
        <Typography component="span" variant="caption"
          sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => {
            import('../../../components/support/SupportCaseDialog').then(({ openSupportCase }) => {
              openSupportCase({
                category: 'feature',
                subject: t('attendance:attendanceReports.supportSubject', { defaultValue: 'Report request' }),
                description: t('attendance:attendanceReports.supportDescription', { defaultValue: 'I would like to request a new attendance report:\n\n[Please describe the report you need — what data, what shape, what time range]' }),
                priority: 'normal',
              });
            });
          }}>
          {t('attendance:attendanceReports.tellUs')}
        </Typography>
      </Typography>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AttendanceReports;
